import { Close, Edit, TableRows } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';
import { ChangeEvent, useState } from 'react';
import * as React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SolaForceHeading } from '~/components/SolaForceHeading';
import { useApplicationConfig } from '~/data/applicationConfig';
import { EnumListKey } from '~/data/enums';
import { TEXT_LONG_MAX_LENGTH } from '~/features/OverlappingEmploymentDialog/data';
import { useEnumFilteredOptions } from '~/features/OverlappingEmploymentDialog/hooks/useEnumFilteredOption';
import { OverlappingEmploymentFormValues } from '~/features/OverlappingEmploymentDialog/hooks/useOverlappingEmploymentForm';
import { convertToMomentOrNull } from '~/utils/helpers/date';

import { useSxStyles } from '../useStyles';

interface Props {
  formValues: OverlappingEmploymentFormValues;
}

export const StartingSalaryForm = ({ formValues }: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { applicationConfig } = useApplicationConfig();
  const bnEnableHourlySalaryComponents =
    applicationConfig?.fComConfig?.miscellaneousConfig
      ?.fComEnableHourlySalaryComponents;

  const {
    formProps: { control, setValue },
  } = formValues;

  const [
    filteredOptionsEventFrequency,
    filteredOptionsSalaryType,
    filteredOptionsSalaryClass,
    filteredOptionsSalaryChangeReason,
    filteredOptionsHourlySalaryComponent,
  ] = useEnumFilteredOptions(control, [
    EnumListKey.EVENT_FREQUENCY,
    EnumListKey.REW_SALARY_TYPE,
    EnumListKey.REW_SALARY_CLASS,
    EnumListKey.SALARY_CHANGE_REASON,
    EnumListKey.HOURLY_SALARY_COMPONENT,
  ]);

  const [
    salaryFrequencySelected,
    currency,
    defaultStartDate,
    salaryStartDate,
    salaryComp1StartDate,
  ] = useWatch({
    control,
    name: [
      'overlappingEmployment.startingSalary.fSalaryProposalFrequency',
      'overlappingEmployment.basicData.fEmplCurrency',
      'overlappingEmployment.basicData.fEmplDateStart',
      'overlappingEmployment.startingSalary.fSalaryProposalStartDate',
      'overlappingEmployment.startingSalary.fSalaryProposalComponents.fSalaryComp1StartDate',
    ],
  });

  const [showRow, setShowRow] = useState(
    !!salaryStartDate || !!salaryComp1StartDate,
  );

  const hourlyFrequencyIsSelected = salaryFrequencySelected === 'HOURLY';
  const showSalaryComponents =
    bnEnableHourlySalaryComponents && hourlyFrequencyIsSelected;

  React.useEffect(() => {
    if (!salaryStartDate && !showSalaryComponents) {
      setValue(
        'overlappingEmployment.startingSalary.fSalaryProposalStartDate',
        defaultStartDate,
      );
    } else if (!salaryComp1StartDate && showSalaryComponents) {
      setValue(
        'overlappingEmployment.startingSalary.fSalaryProposalComponents.fSalaryComp1StartDate',
        defaultStartDate,
      );
    }
  }, [
    defaultStartDate,
    salaryComp1StartDate,
    salaryStartDate,
    setValue,
    showSalaryComponents,
  ]);

  const requiredText = t('fieldIsRequired');
  const dateFormat = 'YYYY-MM-DD';

  const renderHourlySalaryComponents = () => {
    return (
      <>
        {filteredOptionsHourlySalaryComponent
          .slice(1)
          .map((component, index) => {
            return (
              <Box key={component.code} component={'tr'}>
                <Box component={'td'}></Box>
                <Box component={'td'}>{component.name}</Box>
                <Box component={'td'}>
                  <Controller
                    // @ts-ignore
                    name={`overlappingEmployment.startingSalary.fSalaryProposalComponents.fSalaryComp${
                      index + 2
                    }StartDate`}
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        value={
                          value ? moment(value as string, dateFormat) : null
                        }
                        onChange={(v: Moment | null) => {
                          onChange(v?.format(dateFormat));
                        }}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!error,
                            helperText: error?.message,
                            size: 'small',
                            sx: sxStyles.inputField,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    // @ts-ignore
                    name={`overlappingEmployment.startingSalary.fSalaryProposalComponents.fSalaryComp${
                      index + 2
                    }Amount`}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        type="number"
                        value={value}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="start">
                                {currency}
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={sxStyles.inputField}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}></Box>
                <Box component={'td'}></Box>
                <Box component={'td'}></Box>
                <Box component={'td'}></Box>
                <Box component={'td'}></Box>
              </Box>
            );
          })}
      </>
    );
  };

  return (
    <Box>
      <SolaForceHeading
        headingIcon={<TableRows />}
        heading={t('laStartingSalary')}
      >
        {!showRow ? (
          <Button onClick={() => setShowRow(true)} startIcon={<Edit />}>
            {t('laEdit')}
          </Button>
        ) : (
          <Button onClick={() => setShowRow(false)} startIcon={<Close />}>
            {t('laCancel')}
          </Button>
        )}
      </SolaForceHeading>
      {showRow && (
        <Grid sx={{ overflowX: 'auto' }}>
          <Box sx={sxStyles.table} component={'table'}>
            <Box component={'thead'}>
              <Box component={'tr'}>
                <Box component={'th'}>
                  {t('laBaseSalaryFrequency')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>
                  {bnEnableHourlySalaryComponents && t('laComponent')}
                </Box>
                <Box component={'th'}>
                  {t('laStartDate')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>
                  {t('laAmount')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>{t('laType')}</Box>
                <Box component={'th'}>{t('laClass')}</Box>
                <Box component={'th'}>
                  {t('laSalaryChangeReason')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>{t('laInfo')}</Box>
                <Box component={'th'}>{t('laAttachment')}</Box>
              </Box>
            </Box>
            <Box component={'tbody'}>
              <Box component={'tr'}>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.startingSalary.fSalaryProposalFrequency'
                    }
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        options={filteredOptionsEventFrequency}
                        getOptionLabel={(o) => o?.name || ''}
                        isOptionEqualToValue={(o, v) => o?.code === v?.code}
                        onChange={(_, value) => onChange(value?.code || '')}
                        value={
                          filteredOptionsEventFrequency.find(
                            (o) => o?.code === value,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            size="small"
                            error={!!error}
                            helperText={error?.message}
                            sx={sxStyles.inputField}
                          />
                        )}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  {showSalaryComponents &&
                    filteredOptionsHourlySalaryComponent[0].name}
                </Box>
                <Box component={'td'}>
                  <Controller
                    name={
                      showSalaryComponents
                        ? 'overlappingEmployment.startingSalary.fSalaryProposalComponents.fSalaryComp1StartDate'
                        : 'overlappingEmployment.startingSalary.fSalaryProposalStartDate'
                    }
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        value={convertToMomentOrNull(value)}
                        onChange={(v: Moment | null) => {
                          onChange(v?.format(dateFormat));
                        }}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                            size: 'small',
                            sx: sxStyles.inputField,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      showSalaryComponents
                        ? 'overlappingEmployment.startingSalary.fSalaryProposalComponents.fSalaryComp1Amount'
                        : 'overlappingEmployment.startingSalary.fSalaryProposalSalary'
                    }
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        type="number"
                        required
                        value={value}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="start">
                                {currency}
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={sxStyles.inputField}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.startingSalary.fSalaryProposalSalaryType'
                    }
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        options={filteredOptionsSalaryType}
                        getOptionLabel={(o) => o?.name || ''}
                        isOptionEqualToValue={(o, v) => o?.code === v?.code}
                        onChange={(_, value) => onChange(value?.code || '')}
                        value={
                          filteredOptionsSalaryType.find(
                            (o) => o?.code === value,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            error={!!error}
                            helperText={error?.message}
                            sx={sxStyles.inputField}
                          />
                        )}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.startingSalary.fSalaryProposalSalaryClass'
                    }
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        options={filteredOptionsSalaryClass}
                        getOptionLabel={(o) => o?.name || ''}
                        isOptionEqualToValue={(o, v) => o?.code === v?.code}
                        onChange={(_, value) => onChange(value?.code || '')}
                        value={
                          filteredOptionsSalaryClass.find(
                            (o) => o?.code === value,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            error={!!error}
                            helperText={error?.message}
                            sx={sxStyles.inputField}
                          />
                        )}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.startingSalary.fSalaryProposalChangeReason'
                    }
                    rules={{ required: requiredText }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        options={filteredOptionsSalaryChangeReason}
                        getOptionLabel={(o) => o?.name || ''}
                        isOptionEqualToValue={(o, v) => o?.code === v?.code}
                        onChange={(_, value) => onChange(value?.code || '')}
                        value={
                          filteredOptionsSalaryChangeReason.find(
                            (o) => o?.code === value,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            size="small"
                            error={!!error}
                            helperText={error?.message}
                            sx={sxStyles.inputField}
                          />
                        )}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Controller
                    control={control}
                    name={
                      'overlappingEmployment.startingSalary.fSalaryProposalInfo'
                    }
                    rules={{
                      maxLength: {
                        value: TEXT_LONG_MAX_LENGTH,
                        message: t('laMaxLengthRequired'),
                      },
                    }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        value={value}
                        multiline
                        rows={1}
                        size="small"
                        onChange={onChange}
                        sx={sxStyles.inputField}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Box>
                <Box component={'td'}>
                  <Grid
                    display={'flex'}
                    flexWrap={'nowrap'}
                    alignItems={'center'}
                  >
                    <Controller
                      control={control}
                      name={
                        'overlappingEmployment.startingSalary.fEmplAttachment'
                      }
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <>
                          <Box
                            id="starting-salary-attachment"
                            component={'input'}
                            type="file"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              onChange((e.target.files ?? [])[0]);
                            }}
                            sx={sxStyles.hiddenFileInput}
                          />
                          <TextField
                            value={value?.name ?? ''}
                            size="small"
                            onChange={onChange}
                            sx={sxStyles.inputField}
                            inputProps={{ readOnly: true }}
                            error={!!error}
                            helperText={error?.message}
                          />
                        </>
                      )}
                    />
                    <Box
                      component={'label'}
                      htmlFor="starting-salary-attachment"
                    >
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="upload picture"
                        component="span"
                      >
                        <Box
                          component={'i'}
                          sx={sxStyles.blueIcon}
                          className="fa fa-paperclip"
                        ></Box>
                      </IconButton>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setValue(
                          'overlappingEmployment.startingSalary.fEmplAttachmentName',
                          '',
                        );
                        setValue(
                          'overlappingEmployment.startingSalary.fEmplAttachment',
                          undefined,
                        );
                      }}
                    >
                      <Box
                        component={'i'}
                        sx={sxStyles.blueIcon}
                        className="fa fa-trash-o"
                      ></Box>
                    </IconButton>
                  </Grid>
                </Box>
              </Box>
              {showSalaryComponents && renderHourlySalaryComponents()}
            </Box>
          </Box>
        </Grid>
      )}
    </Box>
  );
};
